import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffd93d 0%, #ff6b6b 50%, #6bcf7f 100%);
  border-radius: 20px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 15px;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const Title = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.1rem;
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const EligibilityNote = styled(motion.p)`
  text-align: center;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: white;
  font-weight: bold;
  font-size: 1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #666;
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #666;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(45deg, #6bcf7f, #4ecdc4);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(107, 207, 127, 0.4);
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(107, 207, 127, 0.5);
  }
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(107, 207, 127, 0.9);
  color: white;
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
  font-weight: bold;
  margin-top: 1rem;
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(255, 107, 107, 0.9);
  color: white;
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
  font-weight: bold;
  margin-top: 1rem;
`;

const RaffleForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    walletAddress: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Debug environment variables
          console.log('🔍 All environment variables:');
      console.log('- REACT_APP_EMAILJS_SERVICE_ID:', process.env.REACT_APP_EMAILJS_SERVICE_ID);
      console.log('- REACT_APP_EMAILJS_USER_ID:', process.env.REACT_APP_EMAILJS_USER_ID);
      console.log('- REACT_APP_EMAILJS_USER_CONFIRMATION_TEMPLATE_ID:', process.env.REACT_APP_EMAILJS_USER_CONFIRMATION_TEMPLATE_ID);
      console.log('🔍 Is user template ID undefined?', process.env.REACT_APP_EMAILJS_USER_CONFIRMATION_TEMPLATE_ID === undefined);
      console.log('🔍 Expected template IDs:');
      console.log('- Admin template should be: template_f4igy37');
      console.log('- User template should be: template_ipya20x');

    try {
      // Send admin notification email
      const adminEmailData = {
        service_id: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_a3tooxs',
        template_id: 'template_f4igy37',
        user_id: process.env.REACT_APP_EMAILJS_USER_ID || 'W5xOWdAf2bNJ1pkMu',
        template_params: {
          from_name: formData.name,
          from_email: formData.email,
          wallet_address: formData.walletAddress,
          message: formData.message || 'No message provided',
          to_name: 'Chikin Lady Team',
          // Add additional variables that might be expected
          name: formData.name,
          email: formData.email,
          walletAddress: formData.walletAddress,
          wallet: formData.walletAddress,
          wallet_addr: formData.walletAddress,
          user_message: formData.message || 'No message provided',
          userMessage: formData.message || 'No message provided',
          msg: formData.message || 'No message provided'
        }
      };

      // Send user confirmation email
      const userTemplateId = process.env.REACT_APP_EMAILJS_USER_CONFIRMATION_TEMPLATE_ID;
      console.log('🔍 Debug - User template ID:', userTemplateId);
      console.log('🔍 Debug - Environment variable:', process.env.REACT_APP_EMAILJS_USER_CONFIRMATION_TEMPLATE_ID);
      
      // Use the user template if available, otherwise fallback to admin template
      const finalUserTemplateId = userTemplateId || 'template_ipya20x'; // Hardcode the correct template ID
      console.log('🔍 Debug - Final template ID being used:', finalUserTemplateId);
      
      const userEmailData = {
        service_id: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_a3tooxs',
        template_id: finalUserTemplateId,
        user_id: process.env.REACT_APP_EMAILJS_USER_ID || 'W5xOWdAf2bNJ1pkMu',
        template_params: {
          to_name: formData.name,
          to_email: formData.email,
          wallet_address: formData.walletAddress,
          message: formData.message || 'No message provided',
          from_name: 'Chikin Lady Team',
          // Add additional variables that might be expected by user template
          name: formData.name,
          email: formData.email,
          walletAddress: formData.walletAddress,
          user_message: formData.message || 'No message provided',
          entry_date: new Date().toLocaleDateString()
        }
      };

      console.log('📤 Sending admin email with data:', adminEmailData);
      console.log('📤 Sending user email with data:', userEmailData);
      console.log('🔍 Admin template ID:', adminEmailData.template_id);
      console.log('🔍 User template ID:', userEmailData.template_id);

      // Send both emails using EmailJS
      const [adminResponse, userResponse] = await Promise.all([
        fetch('https://api.emailjs.com/api/v1.0/email/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(adminEmailData)
        }),
        fetch('https://api.emailjs.com/api/v1.0/email/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userEmailData)
        })
      ]);

      console.log('📨 Admin response status:', adminResponse.status);
      console.log('📨 User response status:', userResponse.status);

      if (!adminResponse.ok) {
        const errorData = await adminResponse.text();
        console.error('Admin EmailJS error:', errorData);
        throw new Error('Failed to send admin notification email');
      }

      // TODO: Re-enable user email after fixing template
      // if (!userResponse.ok) {
      //   const errorData = await userResponse.text();
      //   console.error('❌ User EmailJS error:', errorData);
      //   console.error('❌ User email response status:', userResponse.status);
      //   // Don't throw error for user email failure, just log it
      //   console.warn('⚠️ Failed to send user confirmation email, but admin was notified');
      // } else {
      //   console.log('✅ User confirmation email sent successfully!');
      // }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        walletAddress: '',
        message: ''
      });
      
      // Close form after 3 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <FormContainer
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CloseButton
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </CloseButton>
            
            <Title
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              🎟️ NFT Raffle Registration
            </Title>
            
                         <Subtitle
               initial={{ y: -20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               transition={{ delay: 0.3 }}
             >
               Enter for a chance to win exclusive Chikin Lady Merchandise or NFT's!
             </Subtitle>
             
             <EligibilityNote
               initial={{ y: -20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               transition={{ delay: 0.4 }}
             >
               Must own a Chikin Lady NFT to be eligible to win
             </EligibilityNote>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                />
              </FormGroup>
              
                             <FormGroup>
                 <Label>Wallet Address (for OpenSea verification) *</Label>
                 <Input
                   type="text"
                   name="walletAddress"
                   value={formData.walletAddress}
                   onChange={handleInputChange}
                   placeholder="Enter your crypto wallet address"
                   required
                 />
               </FormGroup>
              
              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us why you love Chikin Lady! (optional)"
                />
              </FormGroup>
              
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isSubmitting ? 'Submitting...' : '🎯 Enter Raffle'}
              </SubmitButton>
            </Form>
            
            {submitStatus === 'success' && (
              <SuccessMessage
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                🎉 Registration successful! You're now entered in the raffle!
              </SuccessMessage>
            )}
            
            {submitStatus === 'error' && (
              <ErrorMessage
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                ❌ Something went wrong. Please try again!
              </ErrorMessage>
            )}
          </FormContainer>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default RaffleForm;
